import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';

interface ResizableTableProps {
  children: React.ReactNode;
}

const COLUMN_WIDTHS_KEY = 'fileManagerColumnWidths';
const DEFAULT_COLUMN_WIDTHS: { [key: string]: number } = {
  'column-0': 400, // Name column - wider
  'column-1': 100, // Type column
  'column-2': 100, // Size column
  'column-3': 180, // Last Modified column
  'column-4': 150, // Actions column
};

function loadColumnWidths(): { [key: string]: number } {
  try {
    const raw = localStorage.getItem(COLUMN_WIDTHS_KEY);
    return raw ? { ...DEFAULT_COLUMN_WIDTHS, ...JSON.parse(raw) } : DEFAULT_COLUMN_WIDTHS;
  } catch {
    return DEFAULT_COLUMN_WIDTHS;
  }
}

export const ResizableTable: React.FC<ResizableTableProps> = ({ children }) => {
  const [columnWidths, setColumnWidths] = useState<{ [key: string]: number }>(loadColumnWidths);
  const [isResizing, setIsResizing] = useState(false);
  const [currentColumn, setCurrentColumn] = useState<string | null>(null);
  const startX = useRef<number>(0);
  const startWidth = useRef<number>(0);

  // Remember the user's column widths across sessions.
  useEffect(() => {
    try {
      localStorage.setItem(COLUMN_WIDTHS_KEY, JSON.stringify(columnWidths));
    } catch {
      // ignore storage errors (private mode, quota)
    }
  }, [columnWidths]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing || !currentColumn) return;

    const deltaX = e.clientX - startX.current;
    const newWidth = Math.max(80, startWidth.current + deltaX);

    setColumnWidths(prev => ({
      ...prev,
      [currentColumn]: newWidth
    }));
  }, [isResizing, currentColumn]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    setCurrentColumn(null);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);

  const handleMouseDown = useCallback((e: React.MouseEvent, columnKey: string) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setCurrentColumn(columnKey);
    startX.current = e.clientX;
    startWidth.current = columnWidths[columnKey] || 200;

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [columnWidths, handleMouseMove, handleMouseUp]);

  const getColumnStyle = (columnKey: string) => ({
    width: columnWidths[columnKey] || 'auto',
    minWidth: columnWidths[columnKey] || 'auto',
    position: 'relative' as const,
  });

  // Clone children and add resizer functionality
  const enhancedChildren = React.Children.map(children, (child) => {
    if (React.isValidElement(child) && child.type === TableHead) {
      return React.cloneElement(child as React.ReactElement<any>, {
        children: React.Children.map((child as React.ReactElement<any>).props.children, (row) => {
          if (React.isValidElement(row) && row.type === TableRow) {
            return React.cloneElement(row as React.ReactElement<any>, {
              children: React.Children.map((row as React.ReactElement<any>).props.children, (cell, index) => {
                if (React.isValidElement(cell) && cell.type === TableCell) {
                  const columnKey = `column-${index}`;
                  return React.cloneElement(cell as React.ReactElement<any>, {
                    style: getColumnStyle(columnKey),
                    children: (
                      <>
                        {(cell as React.ReactElement<any>).props.children}
                        {/* Resize handle: rendered with `sx` so the hover cue actually applies
                            (an inline style can't carry a `:hover` selector). */}
                        <Box
                          onMouseDown={(e) => handleMouseDown(e, columnKey)}
                          sx={{
                            position: 'absolute',
                            right: -3,
                            top: 0,
                            bottom: 0,
                            width: 6,
                            cursor: 'col-resize',
                            zIndex: 1,
                            backgroundColor:
                              currentColumn === columnKey ? 'rgba(255, 45, 85, 0.5)' : 'transparent',
                            transition: 'background-color 0.15s',
                            '&:hover': { backgroundColor: 'rgba(255, 45, 85, 0.4)' },
                          }}
                        />
                      </>
                    )
                  });
                }
                return cell;
              })
            });
          }
          return row;
        })
      });
    }
    return child;
  });

  return (
    <TableContainer component={Paper}>
      <Table style={{ tableLayout: 'fixed' }}>
        {enhancedChildren}
      </Table>
    </TableContainer>
  );
};
